import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

function AdminPanel() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [stats, setStats] = useState({
    totalCompanies: 0,
    totalUsers: 0,
    activeTrials: 0,
    recentSignups: 0,
    tierBreakdown: {},
    statusBreakdown: {},
  });
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState({
    tier: 'free',
    status: 'trial',
    trialEndsAt: '',
    productType: '',
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Tab state
  const [activeTab, setActiveTab] = useState('companies');

  // Feature requests state
  const [featureRequests, setFeatureRequests] = useState([]);
  const [featureRequestsLoading, setFeatureRequestsLoading] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [requestFormData, setRequestFormData] = useState({ status: 'new', admin_notes: '' });
  const [savingRequest, setSavingRequest] = useState(false);

  const loadFeatureRequests = useCallback(async () => {
    setFeatureRequestsLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/feature-requests`);
      setFeatureRequests(response.data);
    } catch (err) {
      console.error('Failed to load feature requests:', err);
    } finally {
      setFeatureRequestsLoading(false);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      setError('');
      const [statsRes, companiesRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/admin/stats`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/admin/companies`),
      ]);
      setStats(statsRes.data);
      setCompanies(companiesRes.data);
    } catch (err) {
      console.error('Failed to load admin data:', err);
      if (err.response?.status === 403) {
        setError('Access denied. Super admin privileges required.');
      } else {
        setError('Failed to load data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Redirect if not super admin
    if (user && !user.isSuperAdmin) {
      navigate('/dashboard');
      return;
    }
    loadData();
    loadFeatureRequests(); // Load on mount for accurate tab count
  }, [user, navigate, loadData, loadFeatureRequests]);

  const openEditModal = (company) => {
    setEditingCompany(company);
    setFormData({
      tier: company.subscriptionTier || 'free',
      status: company.subscriptionStatus || 'trial',
      trialEndsAt: company.trialEndsAt ? company.trialEndsAt.split('T')[0] : '',
      productType: company.subscriptionProduct || '',
    });
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingCompany) return;

    setSaving(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/admin/companies/${editingCompany.id}/tier`,
        {
          tier: formData.tier,
          status: formData.status,
          productType: formData.productType,
        }
      );
      setShowModal(false);
      loadData();
    } catch (err) {
      console.error('Failed to update company:', err);
      alert(err.response?.data?.error || 'Failed to update company');
    } finally {
      setSaving(false);
    }
  };


  const openDeleteModal = (company) => {
    if (company.id === 1) {
      alert('Cannot delete the platform owner company.');
      return;
    }
    if (company.id === user.companyId) {
      alert('Cannot delete your own company.');
      return;
    }
    setCompanyToDelete(company);
    setDeleteConfirmText('');
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setCompanyToDelete(null);
    setDeleteConfirmText('');
  };

  const confirmDeleteCompany = async () => {
    if (!companyToDelete || deleteConfirmText !== companyToDelete.name) {
      return;
    }

    setDeleting(companyToDelete.id);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/admin/companies/${companyToDelete.id}`);
      closeDeleteModal();
      loadData();
    } catch (err) {
      console.error('Failed to delete company:', err);
      alert(err.response?.data?.error || 'Failed to delete company');
    } finally {
      setDeleting(null);
    }
  };

  // Feature request handlers
  const openRequestModal = (request) => {
    setEditingRequest(request);
    setRequestFormData({
      status: request.status || 'new',
      admin_notes: request.admin_notes || '',
    });
  };

  const handleRequestFormChange = (e) => {
    const { name, value } = e.target;
    setRequestFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    if (!editingRequest) return;

    setSavingRequest(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/admin/feature-requests/${editingRequest.id}`,
        requestFormData
      );
      setEditingRequest(null);
      loadFeatureRequests();
    } catch (err) {
      console.error('Failed to update feature request:', err);
      alert(err.response?.data?.error || 'Failed to update request');
    } finally {
      setSavingRequest(false);
    }
  };

  const handleDeleteRequest = async (request) => {
    if (!window.confirm('Are you sure you want to delete this feature request?')) {
      return;
    }

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/admin/feature-requests/${request.id}`
      );
      loadFeatureRequests();
    } catch (err) {
      console.error('Failed to delete feature request:', err);
      alert(err.response?.data?.error || 'Failed to delete request');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'new': return 'request-status new';
      case 'under_review': return 'request-status under-review';
      case 'planned': return 'request-status planned';
      case 'in_progress': return 'request-status in-progress';
      case 'completed': return 'request-status completed';
      case 'declined': return 'request-status declined';
      default: return 'request-status new';
    }
  };

  const formatStatusText = (status) => {
    switch (status) {
      case 'new': return 'New';
      case 'under_review': return 'Under Review';
      case 'planned': return 'Planned';
      case 'in_progress': return 'In Progress';
      case 'completed': return 'Completed';
      case 'declined': return 'Declined';
      default: return 'New';
    }
  };

  const getTierBadgeClass = (tier) => {
    switch (tier) {
      case 'premium': return 'tier-badge premium';
      case 'pro': return 'tier-badge pro';
      case 'basic': return 'tier-badge basic';
      default: return 'tier-badge free';
    }
  };

  const getProductBadgeClass = (product) => {
    switch (product) {
      case 'lawn': return 'product-badge lawn';
      case 'both': return 'product-badge both';
      default: return 'product-badge snow';
    }
  };

  const getProductText = (product) => {
    switch (product) {
      case 'lawn': return 'Lawn';
      case 'both': return 'Both';
      default: return 'Snow';
    }
  };

  // Render product badges - show both for 'both' type
  const renderProductBadges = (product) => {
    if (product === 'both') {
      return (
        <>
          <span className="product-badge snow">Snow</span>
          <span className="product-badge lawn" style={{ marginLeft: '4px' }}>Lawn</span>
        </>
      );
    }
    return (
      <span className={getProductBadgeClass(product)}>
        {getProductText(product)}
      </span>
    );
  };

  const getStatusBadgeClass = (status, stormEventsUsed = 0) => {
    if (status === 'trial' && stormEventsUsed >= 1) {
      return 'status-badge expired';
    }
    switch (status) {
      case 'active': return 'status-badge active';
      case 'past_due': return 'status-badge past-due';
      case 'cancelled': return 'status-badge cancelled';
      case 'trial': return 'status-badge trial';
      default: return 'status-badge trial';
    }
  };

  const getStatusText = (status, stormEventsUsed = 0) => {
    if (status === 'trial' && stormEventsUsed >= 1) {
      return 'Expired';
    }
    switch (status) {
      case 'active': return 'Active';
      case 'past_due': return 'Past Due';
      case 'cancelled': return 'Cancelled';
      case 'trial': return 'Trial';
      default: return 'Trial';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    // Add noon time to prevent UTC offset issues for date-only strings
    // If string already has time info (contains 'T'), use as-is
    const dateToUse = dateStr.includes('T') ? dateStr : dateStr + 'T12:00:00';
    return new Date(dateToUse).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading admin panel...</p>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  if (error && !companies.length) {
    return (
      <div className="admin-page">
        <div className="error-container">
          <h2>Access Denied</h2>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </button>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  // Calculate product breakdown from companies
  const productBreakdown = {
    snow: companies.filter(c => c.subscriptionProduct === 'snow').length,
    lawn: companies.filter(c => c.subscriptionProduct === 'lawn').length,
    both: companies.filter(c => c.subscriptionProduct === 'both').length,
  };

  return (
    <div className="admin-page">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-content">
          <h1 className="admin-logo">Platform Admin</h1>
          <nav className="admin-nav">
            <a href="https://snowtrack.pro" target="_blank" rel="noopener noreferrer" className="nav-link app-link snow-link">
              SnowTrack
            </a>
            <a href="https://lawntrack.pro" target="_blank" rel="noopener noreferrer" className="nav-link app-link lawn-link">
              LawnTrack
            </a>
            <button onClick={() => navigate('/dashboard')} className="nav-link">
              Dashboard
            </button>
            <button onClick={logout} className="nav-link logout-link">
              Logout
            </button>
          </nav>
        </div>
      </header>

      <main className="admin-main">
        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.totalCompanies}</div>
            <div className="stat-label">Total Companies</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.statusBreakdown?.active || 0}</div>
            <div className="stat-label">Active Subscriptions</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.activeTrials}</div>
            <div className="stat-label">Active Trials</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.totalUsers}</div>
            <div className="stat-label">Total Users</div>
          </div>
        </div>

        {/* Product Breakdown */}
        <div className="breakdown-row">
          <div className="breakdown-section">
            <h3>Product Breakdown</h3>
            <div className="tier-chips">
              <span className="tier-chip snow-chip">
                SnowTrack: {productBreakdown.snow}
              </span>
              <span className="tier-chip lawn-chip">
                LawnTrack: {productBreakdown.lawn}
              </span>
              <span className="tier-chip both-chip">
                Both: {productBreakdown.both}
              </span>
            </div>
          </div>
          <div className="breakdown-section">
            <h3>Tier Breakdown</h3>
            <div className="tier-chips">
              <span className="tier-chip free">
                Free: {stats.tierBreakdown?.free || 0}
              </span>
              <span className="tier-chip basic">
                Basic: {stats.tierBreakdown?.basic || 0}
              </span>
              <span className="tier-chip pro">
                Pro: {stats.tierBreakdown?.pro || 0}
              </span>
              <span className="tier-chip premium">
                Premium: {stats.tierBreakdown?.premium || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="admin-tabs">
          <button
            className={`admin-tab ${activeTab === 'companies' ? 'active' : ''}`}
            onClick={() => setActiveTab('companies')}
          >
            Companies ({companies.length})
          </button>
          <button
            className={`admin-tab ${activeTab === 'feature-requests' ? 'active' : ''}`}
            onClick={() => setActiveTab('feature-requests')}
          >
            Feature Requests ({featureRequests.length})
          </button>
        </div>

        {/* Companies Table */}
        {activeTab === 'companies' && (
        <div className="companies-section">
          <h2>Companies ({companies.length})</h2>

          <div className="table-container">
            <table className="companies-table">
              <thead>
                <tr>
                  <th>Company Name</th>
                  <th>Product</th>
                  <th>Tier</th>
                  <th>Status</th>
                  <th>Customers</th>
                  <th>Users</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {companies.map(company => (
                  <tr key={company.id}>
                    <td className="company-name-cell">
                      <div className="company-name">{company.name}</div>
                    </td>
                    <td>
                      {renderProductBadges(company.subscriptionProduct)}
                    </td>
                    <td>
                      <span className={getTierBadgeClass(company.subscriptionTier)}>
                        {company.subscriptionTier || 'free'}
                      </span>
                    </td>
                    <td>
                      <span className={getStatusBadgeClass(company.subscriptionStatus, company.stormEventsUsed)}>
                        {getStatusText(company.subscriptionStatus, company.stormEventsUsed)}
                      </span>
                    </td>
                    <td>{company.customerCount}</td>
                    <td>{company.userCount}</td>
                    <td>{formatDate(company.createdAt)}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => openEditModal(company)}
                      >
                        Edit Tier
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => openDeleteModal(company)}
                        disabled={deleting === company.id || company.id === 1}
                        style={{ marginLeft: '8px' }}
                      >
                        {deleting === company.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {companies.length === 0 && (
            <div className="empty-state">
              <p>No companies found.</p>
            </div>
          )}
        </div>
        )}

        {/* Feature Requests Tab */}
        {activeTab === 'feature-requests' && (
          <div className="feature-requests-section">
            <h2>Feature Requests</h2>

            {featureRequestsLoading ? (
              <div className="loading-state">Loading feature requests...</div>
            ) : featureRequests.length === 0 ? (
              <div className="empty-state">
                <p>No feature requests yet.</p>
              </div>
            ) : (
              <div className="requests-list">
                {featureRequests.map(request => (
                  <div key={request.id} className="request-card">
                    <div className="request-header">
                      <div className="request-header-left">
                        <span className={getStatusBadge(request.status)}>
                          {formatStatusText(request.status)}
                        </span>
                        {request.product && (
                          <span className={`product-badge ${request.product === 'lawn' ? 'lawn' : request.product === 'both' ? 'both' : 'snow'}`}>
                            {request.product === 'lawn' ? 'Lawn' : request.product === 'both' ? 'Both' : 'Snow'}
                          </span>
                        )}
                      </div>
                      <span className="request-date">{formatDate(request.created_at)}</span>
                    </div>
                    <h3 className="request-subject">{request.subject}</h3>
                    <p className="request-description">{request.description}</p>
                    <div className="request-meta">
                      <span className="request-company">{request.company_name}</span>
                      <span className="request-user">{request.user_name || 'Unknown'}</span>
                    </div>
                    {request.admin_notes && (
                      <div className="request-notes">
                        <strong>Notes:</strong> {request.admin_notes}
                      </div>
                    )}
                    <div className="request-actions">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => openRequestModal(request)}
                      >
                        Update Status
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteRequest(request)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Edit Tier Modal */}
      {showModal && editingCompany && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Subscription</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <p className="modal-company-name">{editingCompany.name}</p>

                <div className="form-group">
                  <label className="form-label">Subscription Tier</label>
                  <select
                    name="tier"
                    className="form-select"
                    value={formData.tier}
                    onChange={handleFormChange}
                  >
                    <option value="free">Free</option>
                    <option value="basic">Basic</option>
                    <option value="pro">Pro</option>
                    <option value="premium">Premium</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    name="status"
                    className="form-select"
                    value={formData.status}
                    onChange={handleFormChange}
                  >
                    <option value="trial">Trial</option>
                    <option value="active">Active</option>
                    <option value="past_due">Past Due</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Product Type</label>
                  <select
                    name="productType"
                    className="form-select"
                    value={formData.productType}
                    onChange={handleFormChange}
                  >
                    <option value="" disabled>Select product...</option>
                    <option value="snow">Snow</option>
                    <option value="lawn">Lawn</option>
                    <option value="both">Both</option>
                  </select>
                </div>

                {formData.status === 'trial' && (
                  <div className="form-group">
                    <label className="form-label">Trial Ends</label>
                    <input
                      type="date"
                      name="trialEndsAt"
                      className="form-input"
                      value={formData.trialEndsAt}
                      onChange={handleFormChange}
                    />
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Company Confirmation Modal */}
      {showDeleteModal && companyToDelete && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal delete-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header delete-header">
              <h2>Delete Company</h2>
              <button className="modal-close" onClick={closeDeleteModal}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="delete-warning">
                <svg className="warning-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
                <p>This action cannot be undone.</p>
              </div>
              <p className="delete-info">
                This will permanently delete <strong>{companyToDelete.name}</strong> and all their:
              </p>
              <ul className="delete-list">
                <li>Users and accounts</li>
                <li>Customers and properties</li>
                <li>Routes and services</li>
                <li>Invoices and expenses</li>
              </ul>
              <div className="form-group">
                <label className="form-label">
                  To confirm, type <strong>{companyToDelete.name}</strong> below:
                </label>
                <input
                  type="text"
                  className="form-input delete-input"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type company name to confirm"
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeDeleteModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmDeleteCompany}
                disabled={deleteConfirmText !== companyToDelete.name || deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Company'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feature Request Modal */}
      {editingRequest && (
        <div className="modal-overlay" onClick={() => setEditingRequest(null)}>
          <div className="modal feature-request-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Update Feature Request</h2>
              <button className="modal-close" onClick={() => setEditingRequest(null)}>&times;</button>
            </div>
            <form onSubmit={handleRequestSubmit}>
              <div className="modal-body">
                <div className="request-preview">
                  <h4>{editingRequest.subject}</h4>
                  <p>{editingRequest.description}</p>
                  <div className="request-meta">
                    <span>{editingRequest.company_name}</span>
                    <span>{editingRequest.user_name}</span>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    name="status"
                    className="form-select"
                    value={requestFormData.status}
                    onChange={handleRequestFormChange}
                  >
                    <option value="new">New</option>
                    <option value="under_review">Under Review</option>
                    <option value="planned">Planned</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="declined">Declined</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Admin Notes</label>
                  <textarea
                    name="admin_notes"
                    className="form-textarea"
                    value={requestFormData.admin_notes}
                    onChange={handleRequestFormChange}
                    rows={3}
                    placeholder="Internal notes about this request..."
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setEditingRequest(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={savingRequest}
                >
                  {savingRequest ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{styles}</style>
    </div>
  );
}

const styles = `
  .admin-page {
    min-height: 100vh;
    background: #f3f4f6;
  }

  .loading-container, .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    padding: 20px;
    text-align: center;
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e5e7eb;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* Header - Neutral Platform Branding */
  .admin-header {
    background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
    color: white;
    padding: 16px 24px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  }

  .admin-header-content {
    max-width: 1400px;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .admin-header h1.admin-logo {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0;
    color: #ffffff !important;
  }

  .admin-nav {
    display: flex;
    gap: 8px;
  }

  .nav-link {
    background: rgba(255,255,255,0.1);
    border: none;
    color: white;
    padding: 8px 16px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.875rem;
    transition: background 0.15s;
    text-decoration: none;
  }

  .nav-link:hover {
    background: rgba(255,255,255,0.2);
  }

  .app-link.snow-link {
    background: rgba(14, 165, 233, 0.3);
  }

  .app-link.snow-link:hover {
    background: rgba(14, 165, 233, 0.5);
  }

  .app-link.lawn-link {
    background: rgba(34, 197, 94, 0.3);
  }

  .app-link.lawn-link:hover {
    background: rgba(34, 197, 94, 0.5);
  }

  .logout-link {
    background: rgba(239, 68, 68, 0.2);
  }

  .logout-link:hover {
    background: rgba(239, 68, 68, 0.3);
  }

  /* Main Content */
  .admin-main {
    max-width: 1400px;
    margin: 0 auto;
    padding: 24px;
  }

  /* Stats Grid */
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 24px;
  }

  @media (max-width: 1024px) {
    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 640px) {
    .stats-grid {
      grid-template-columns: 1fr;
    }
  }

  .stat-card {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  }

  .stat-value {
    font-size: 2rem;
    font-weight: 700;
    color: #374151;
  }

  .stat-label {
    font-size: 0.875rem;
    color: #6b7280;
    margin-top: 4px;
  }

  .stat-note {
    font-size: 0.75rem;
    color: #9ca3af;
    font-style: italic;
    margin-top: 4px;
  }

  /* Breakdown Row */
  .breakdown-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 24px;
  }

  @media (max-width: 768px) {
    .breakdown-row {
      grid-template-columns: 1fr;
    }
  }

  .breakdown-section {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  }

  .breakdown-section h3 {
    margin: 0 0 12px 0;
    font-size: 1rem;
    color: #374151;
  }

  .tier-chips {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  .tier-chip {
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 0.875rem;
    font-weight: 500;
  }

  .tier-chip.free {
    background: #f3f4f6;
    color: #6b7280;
  }

  .tier-chip.basic {
    background: #dbeafe;
    color: #1e40af;
  }

  .tier-chip.pro {
    background: #ede9fe;
    color: #6d28d9;
  }

  .tier-chip.premium {
    background: #fef3c7;
    color: #92400e;
  }

  .tier-chip.snow-chip {
    background: #e0f2fe;
    color: #0369a1;
  }

  .tier-chip.lawn-chip {
    background: #dcfce7;
    color: #166534;
  }

  .tier-chip.both-chip {
    background: linear-gradient(135deg, #e0f2fe 0%, #dcfce7 100%);
    color: #374151;
  }

  /* Companies Section */
  .companies-section {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  }

  .companies-section h2 {
    margin: 0 0 16px 0;
    font-size: 1.25rem;
    color: #1f2937;
  }

  .table-container {
    overflow-x: auto;
  }

  .companies-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
  }

  .companies-table th {
    text-align: left;
    padding: 12px;
    background: #f9fafb;
    border-bottom: 2px solid #e5e7eb;
    font-weight: 600;
    color: #374151;
    white-space: nowrap;
  }

  .companies-table td {
    padding: 12px;
    border-bottom: 1px solid #e5e7eb;
    vertical-align: middle;
  }

  .companies-table tr:hover {
    background: #f9fafb;
  }

  .company-name-cell .company-name {
    font-weight: 500;
    color: #1f2937;
  }

  /* Tier Badges */
  .tier-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: capitalize;
  }

  .tier-badge.free {
    background: #f3f4f6;
    color: #6b7280;
  }

  .tier-badge.basic {
    background: #dbeafe;
    color: #1e40af;
  }

  .tier-badge.pro {
    background: #ede9fe;
    color: #6d28d9;
  }

  .tier-badge.premium {
    background: linear-gradient(135deg, #fef3c7, #fde68a);
    color: #92400e;
  }

  /* Product Badges */
  .product-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .product-badge.snow {
    background: #e0f2fe;
    color: #0369a1;
  }

  .product-badge.lawn {
    background: #dcfce7;
    color: #166534;
  }

  .product-badge.bundle {
    background: linear-gradient(135deg, #e0f2fe 0%, #dcfce7 100%);
    color: #1e3a5f;
  }

  /* Status Badges */
  .status-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .status-badge.trial {
    background: #fef3c7;
    color: #92400e;
  }

  .status-badge.active {
    background: #d1fae5;
    color: #065f46;
  }

  .status-badge.past-due {
    background: #fee2e2;
    color: #991b1b;
  }

  .status-badge.cancelled {
    background: #f3f4f6;
    color: #6b7280;
  }

  .status-badge.expired {
    background: #fee2e2;
    color: #dc2626;
    font-weight: 500;
  }

  /* Buttons */
  .btn {
    padding: 10px 20px;
    border: none;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
  }

  .btn-sm {
    padding: 6px 12px;
    font-size: 0.8rem;
  }

  .btn-primary {
    background: #3b82f6;
    color: white;
  }

  .btn-primary:hover {
    background: #2563eb;
  }

  .btn-primary:disabled {
    background: #93c5fd;
    cursor: not-allowed;
  }

  .btn-secondary {
    background: #e5e7eb;
    color: #374151;
  }

  .btn-secondary:hover {
    background: #d1d5db;
  }

  /* Empty State */
  .empty-state {
    text-align: center;
    padding: 40px 20px;
    color: #6b7280;
  }

  /* Modal */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
  }

  .modal {
    background: white;
    border-radius: 12px;
    max-width: 450px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid #e5e7eb;
  }

  .modal-header h2 {
    margin: 0;
    font-size: 1.25rem;
    color: #1f2937;
  }

  .modal-close {
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #6b7280;
    cursor: pointer;
    line-height: 1;
  }

  .modal-close:hover {
    color: #1f2937;
  }

  .modal-body {
    padding: 20px;
  }

  .modal-company-name {
    font-weight: 600;
    color: #1e3a5f;
    margin: 0 0 16px 0;
    padding-bottom: 12px;
    border-bottom: 1px solid #e5e7eb;
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 16px 20px;
    border-top: 1px solid #e5e7eb;
  }

  /* Form Elements */
  .form-group {
    margin-bottom: 16px;
  }

  .form-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 6px;
  }

  .form-select, .form-input {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
    background: white;
  }


  /* Delete Modal Styles */
  .delete-modal {
    max-width: 480px;
  }

  .delete-header {
    background: #fef2f2;
    border-bottom: 1px solid #fecaca;
  }

  .delete-header h2 {
    color: #dc2626;
  }

  .delete-warning {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    margin-bottom: 16px;
  }

  .warning-icon {
    width: 24px;
    height: 24px;
    color: #dc2626;
    flex-shrink: 0;
  }

  .delete-warning p {
    margin: 0;
    color: #dc2626;
    font-weight: 500;
  }

  .delete-info {
    margin-bottom: 12px;
    color: #374151;
  }

  .delete-list {
    margin: 0 0 20px 0;
    padding-left: 24px;
    color: #6b7280;
  }

  .delete-list li {
    margin-bottom: 4px;
  }

  .delete-input {
    border-color: #fca5a5;
  }

  .delete-input:focus {
    border-color: #dc2626;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
  }

  .form-select:focus, .form-input:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  .form-textarea {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
    font-family: inherit;
    resize: vertical;
    min-height: 80px;
  }

  .form-textarea:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  /* Admin Tabs */
  .admin-tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
  }

  .admin-tab {
    padding: 12px 24px;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 500;
    color: #6b7280;
    cursor: pointer;
    transition: all 0.15s;
  }

  .admin-tab:hover {
    background: #f9fafb;
    color: #374151;
  }

  .admin-tab.active {
    background: #374151;
    color: white;
    border-color: #374151;
  }

  /* Feature Requests Section */
  .feature-requests-section {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  }

  .feature-requests-section h2 {
    margin: 0 0 16px 0;
    font-size: 1.25rem;
    color: #1f2937;
  }

  .loading-state {
    text-align: center;
    padding: 40px;
    color: #6b7280;
  }

  .requests-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .request-card {
    padding: 16px;
    background: #f9fafb;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
  }

  .request-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  .request-status {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .request-status.new {
    background: #dbeafe;
    color: #1e40af;
  }

  .request-status.under-review {
    background: #fef3c7;
    color: #92400e;
  }

  .request-status.planned {
    background: #ede9fe;
    color: #6d28d9;
  }

  .request-status.in-progress {
    background: #e0f2fe;
    color: #0369a1;
  }

  .request-status.completed {
    background: #d1fae5;
    color: #065f46;
  }

  .request-status.declined {
    background: #f3f4f6;
    color: #6b7280;
  }

  .request-date {
    font-size: 0.8rem;
    color: #9ca3af;
  }

  .request-subject {
    margin: 0 0 8px 0;
    font-size: 1rem;
    color: #1f2937;
  }

  .request-description {
    margin: 0 0 12px 0;
    font-size: 0.875rem;
    color: #4b5563;
    line-height: 1.5;
  }

  .request-meta {
    display: flex;
    gap: 16px;
    font-size: 0.8rem;
    color: #6b7280;
    margin-bottom: 12px;
  }

  .request-company {
    font-weight: 500;
  }

  .request-notes {
    margin-bottom: 12px;
    padding: 8px 12px;
    background: #fef3c7;
    border-radius: 6px;
    font-size: 0.8rem;
    color: #92400e;
  }

  .request-actions {
    display: flex;
    gap: 8px;
  }

  .btn-danger {
    background: #dc2626;
    color: white;
    border-color: #dc2626;
  }

  .btn-danger:hover {
    background: #b91c1c;
    border-color: #b91c1c;
  }

  /* Feature Request Modal */
  .feature-request-modal {
    max-width: 500px;
  }

  .request-preview {
    padding: 12px 16px;
    background: #f3f4f6;
    border-radius: 8px;
    margin-bottom: 16px;
  }

  .request-preview h4 {
    margin: 0 0 8px 0;
    color: #1f2937;
    font-size: 0.95rem;
  }

  .request-preview p {
    margin: 0 0 8px 0;
    font-size: 0.85rem;
    color: #4b5563;
    line-height: 1.4;
  }

  .request-preview .request-meta {
    margin-bottom: 0;
  }

  .btn-danger {
    background: #dc2626;
    color: white;
  }

  .btn-danger:hover {
    background: #b91c1c;
  }

  .btn-danger:disabled {
    background: #fca5a5;
    cursor: not-allowed;
  }
`;

export default AdminPanel;
